import React, { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { apiClient } from '../../config/api';
import { useAuth } from '../../contexts/AuthContext';
import { 
  Shield, 
  Users, 
  Settings, 
  CreditCard,
  FileText,
  Building2,
  Trash2,
  ScrollText
} from 'lucide-react';

// Import admin page components
import UserManagement from './UserManagement';
import SystemSettings from './SystemSettings';
import UserPermissions from './UserPermissions';
import CompanyCOTManagement from './CompanyCOTManagement';
import CompanyProfile from './CompanyProfile';
import TrashManagement from './TrashManagement';
import AuditLog from './AuditLog';

const Admin = () => {
  const { user, hasRole } = useAuth();
  const [activeTab, setActiveTab] = useState('overview');
  const { data: adminStats } = useQuery({
    queryKey: ['admin-overview-stats'],
    queryFn: async () => (await apiClient.get('/api/admin/stats')).data.stats,
  });

  const adminSections = [
    {
      id: 'overview',
      name: 'System Overview',
      icon: Shield,
      roles: ['ADMIN'],
      description: 'System health, statistics, and general information'
    },
    {
      id: 'users',
      name: 'User Management',
      icon: Users,
      roles: ['ADMIN'],
      description: 'Manage user accounts and assign system roles (Admin, Staff, Customer)'
    },
    {
      id: 'permissions',
      name: 'User Permissions',
      icon: Shield,
      roles: ['ADMIN'],
      description: 'Grant and revoke granular permissions for fine-grained access control'
    },
    {
      id: 'company-profile',
      name: 'Company Profile',
      icon: Building2,
      roles: ['ADMIN'],
      description: 'The company identity used across the app, invoices, and reports'
    },
    {
      id: 'company-cot',
      name: 'Company COT',
      icon: Building2,
      roles: ['ADMIN'],
      description: 'Manage your organization\'s Class of Trade classification'
    },
    {
      id: 'system',
      name: 'System Settings',
      icon: Settings,
      roles: ['ADMIN'],
      description: 'System configuration and maintenance'
    },
    {
      id: 'audit',
      name: 'Audit Log',
      icon: ScrollText,
      roles: ['ADMIN'],
      description: 'Full trail of who did what and when across the system'
    },
    {
      id: 'trash',
      name: 'Trash',
      icon: Trash2,
      roles: ['ADMIN'],
      description: 'Restore or permanently purge records marked for deletion'
    }
  ];

  const getAccessibleSections = () => {
    return adminSections.filter(section => {
      if (section.roles.includes('ADMIN')) return true;
      if (section.roles.includes('STAFF') && hasRole('STAFF')) return true;
      if (section.roles.includes('CUSTOMER') && hasRole('CUSTOMER')) return true;
      return false;
    });
  };

  const accessibleSections = getAccessibleSections();

  const renderTabContent = () => {
    switch (activeTab) {
      case 'overview':
        return (
          <div className="space-y-6">
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
              <div className="bg-white p-6 rounded-lg shadow">
                <div className="flex items-center">
                  <Users className="h-8 w-8 text-blue-500" />
                  <div className="ml-4">
                    <p className="text-sm font-medium text-gray-600">Total Customers</p>
                    <p className="text-2xl font-semibold text-gray-900">{adminStats?.totalCustomers ?? '-'}</p>
                  </div>
                </div>
              </div>
              <div className="bg-white p-6 rounded-lg shadow">
                <div className="flex items-center">
                  <FileText className="h-8 w-8 text-green-500" />
                  <div className="ml-4">
                    <p className="text-sm font-medium text-gray-600">Active Contracts</p>
                    <p className="text-2xl font-semibold text-gray-900">{adminStats?.totalContracts ?? '-'}</p>
                  </div>
                </div>
              </div>
              <div className="bg-white p-6 rounded-lg shadow">
                <div className="flex items-center">
                  <CreditCard className="h-8 w-8 text-purple-500" />
                  <div className="ml-4">
                    <p className="text-sm font-medium text-gray-600">Total Chargebacks</p>
                    <p className="text-2xl font-semibold text-gray-900">{adminStats?.totalChargebacks ?? '-'}</p>
                  </div>
                </div>
              </div>
            </div>
            
            <div className="bg-white p-6 rounded-lg shadow">
              <h3 className="text-lg font-medium text-gray-900 mb-4">System Status</h3>
              <div className="space-y-3">
                <div className="flex items-center justify-between">
                  <span className="text-sm text-gray-600">Database Connection</span>
                  <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-800">
                    Healthy
                  </span>
                </div>
                <div className="flex items-center justify-between">
                  <span className="text-sm text-gray-600">EDI Service</span>
                  <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-800">
                    Active
                  </span>
                </div>
                <div className="flex items-center justify-between">
                  <span className="text-sm text-gray-600">File Processing</span>
                  <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-yellow-100 text-yellow-800">
                    Processing
                  </span>
                </div>
              </div>
            </div>
          </div>
        );

      case 'users':
        return <UserManagement />;

      case 'permissions':
        return <UserPermissions />;

      case 'company-profile':
        return <CompanyProfile />;

      case 'company-cot':
        return <CompanyCOTManagement />;

      case 'system':
        return <SystemSettings />;

      case 'audit':
        return <AuditLog />;

      case 'trash':
        return <TrashManagement />;

      default:
        return (
          <div className="bg-white p-6 rounded-lg shadow">
            <p className="text-gray-600">Select a section to view details.</p>
          </div>
        );
    }
  };

  return (
    <div className="p-6">
      <div className="mb-6">
        <h1 className="text-2xl font-bold text-gray-900">Admin Panel</h1>
        <p className="text-gray-600 mt-2">
          Welcome, {user?.email} - Role: {user?.roles?.join(', ')}
        </p>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-4 gap-6">
        {/* Sidebar Navigation */}
        <div className="lg:col-span-1">
          <div className="bg-white rounded-lg shadow">
            <div className="p-4 border-b border-gray-200">
              <h2 className="text-lg font-medium text-gray-900">Admin Sections</h2>
            </div>
            <nav className="p-2">
              {accessibleSections.map((section) => (
                <button
                  key={section.id}
                  title={section.name}
                  onClick={() => setActiveTab(section.id)}
                  className={`w-full text-left px-3 py-2 text-sm font-medium rounded-md transition-colors ${
                    activeTab === section.id
                      ? 'bg-primary-100 text-primary-900 border-l-4 border-primary-500'
                      : 'text-gray-600 hover:bg-gray-50 hover:text-gray-900'
                  }`}
                >
                  <div className="flex items-center">
                    <section.icon className="h-4 w-4 mr-3 flex-shrink-0" />
                    <span>{section.name}</span>
                  </div>
                  <p className="text-xs text-gray-500 mt-1 ml-7">{section.description}</p>
                </button>
              ))}
            </nav>
          </div>
        </div>

        {/* Main Content */}
        <div className="lg:col-span-3">
          {renderTabContent()}
        </div>
      </div>
    </div>
  );
};

export default Admin;
